import { useState } from "react";
import { useTranslate } from "react-admin";

import DoneIcon from "@mui/icons-material/Done";

import { Button, Dialog, DialogTitle } from "../MuiGenerals";
import { useAlerting } from "../../hooks";
import { copyToClipboard, toDisplayTime } from "../../utils/helper";
import { useZusDialogStore } from "../../zustand/store";

import RiskLevelBadge from "./RiskLevelBadge";
import StatusBadge from "./StatusBadge";

interface ProviderResponseDialogProps {}

const ProviderResponseDialog = (_: ProviderResponseDialogProps) => {
  const translate = useTranslate();
  const t = (key: string) => translate(`walletManagement.${key}`);
  const zusDialog = useZusDialogStore();
  const { alerting } = useAlerting();
  const [copied, setCopied] = useState(false);

  const isOpen = zusDialog.matchExtra("providerResponseDialog");
  const {
    current_risk_level,
    returned_risk_level,
    rescan_time,
    triggered_by,
    outcome,
    raw_response,
    error_reason,
  } = zusDialog.meta || {};

  const handleClose = () => zusDialog.closeExtra();

  const handleCopy = async () => {
    try {
      // Delegate to the project's `copyToClipboard` helper, which already
      // handles the secure-context fallback (textarea + `document.execCommand`
      // for browsers without `navigator.clipboard`). Reaching for the raw
      // API here threw "Cannot read properties of undefined (reading
      // 'writeText')" in some embedded contexts.
      await copyToClipboard(JSON.stringify(raw_response, null, 2));
      setCopied(true);
      alerting("success", translate("report.copied"));
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      alerting("warning", "Copy failed");
    }
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>{t("merkle_science_response")}</DialogTitle>

      <div style={{ padding: "0 24px 12px" }}>
        <div style={rowStyle}>
          <div style={labelStyle}>{t("walletAddress")}</div>
          <div style={valueStyle}>{zusDialog.meta?.wallet_address}</div>
        </div>
        <div style={rowStyle}>
          <div style={labelStyle}>{t("rescan_time")}</div>
          <div style={valueStyle}>
            {rescan_time ? toDisplayTime(rescan_time) : ""}
          </div>
        </div>
        <div style={rowStyle}>
          <div style={labelStyle}>{t("rescan_triggered_by")}</div>
          <div style={valueStyle}>{triggered_by}</div>
        </div>
        <div style={rowStyle}>
          <div style={labelStyle}>{t("returned_level")}</div>
          <div
            style={{
              ...valueStyle,
              display: "flex",
              gap: 8,
              alignItems: "center",
            }}
          >
            <span
              style={{
                textDecoration: "line-through",
                color: "#8C8C8C",
              }}
            >
              <RiskLevelBadge riskLevel={current_risk_level} />
            </span>
            <span>→</span>
            <RiskLevelBadge riskLevel={returned_risk_level} />
          </div>
        </div>
        {outcome && (
          <div style={rowStyle}>
            <div style={labelStyle}>{t("outcome")}</div>
            <div style={valueStyle}>
              <StatusBadge status={outcome} />
            </div>
          </div>
        )}
        {/* {error_reason && (
          <div style={rowStyle}>
            <div style={labelStyle}>{t("error_reason")}</div>
            <div style={{ ...valueStyle, color: "#CF1322" }}>
              {error_reason}
            </div>
          </div>
        )} */}
      </div>

      <div style={{ padding: "0 24px" }}>
        <div
          style={{
            color: "#8C8C8C",
            fontSize: "12px",
            padding: "8px 0",
            borderBottom: "1px solid #D9D9D9",
          }}
        >
          {t("raw_response_stored_as_received")}
        </div>
      </div>

      <pre
        style={{
          margin: "12px 24px",
          padding: "16px",
          backgroundColor: "#FAFAFA",
          border: "1px solid #E8E8E8",
          borderRadius: "4px",
          fontSize: "12px",
          lineHeight: 1.6,
          maxHeight: "320px",
          overflow: "auto",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          fontFamily:
            'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
        }}
      >
        {JSON.stringify(raw_response, null, 2)}
      </pre>

      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: "10px",
          padding: "12px 24px 20px",
        }}
      >
        <Button
          color="secondary"
          variant="contained"
          onClick={handleCopy}
          disabled={copied}
        >
          {copied ? <DoneIcon fontSize="small" /> : t("copy")}
        </Button>
        <Button color="error" variant="contained" onClick={handleClose}>
          {t("close")}
        </Button>
      </div>
    </Dialog>
  );
};

const rowStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "120px 1fr",
  gap: "12px",
  padding: "6px 0",
  alignItems: "center",
};

const labelStyle: React.CSSProperties = {
  color: "#8C8C8C",
  fontSize: "13px",
};

const valueStyle: React.CSSProperties = {
  color: "#262626",
  fontSize: "13px",
  wordBreak: "break-all",
};

export default ProviderResponseDialog;
